import type { AppIconName } from "@shared/ui/chakra/data-display/icon/icon.tsx";

import type { ReactNode } from "react";

import { HStack, VStack } from "@chakra-ui/react";

import { Icon, Typography } from "@shared/ui/chakra";

export type BannerVariant =
  | "blocked"
  | "error"
  | "errorSecond"
  | "errorSolid"
  | "info"
  | "subtle"
  | "subtleWarning"
  | "success"
  | "warning";
export type BannerVariantConfig = {
  backgroundColor: string;
  borderColor?: string;
  descriptionColor?: string;
  iconBgColor?: string;
  iconColor: string;
  titleColor?: string;
};

const variantMap: Record<BannerVariant, BannerVariantConfig> = {
  blocked: {
    backgroundColor: "fg.warning",
    descriptionColor: "fg.muted",
    iconBgColor: "bg.subtle",
    iconColor: "fg.warning",
    titleColor: "fg.inverted",
  },
  error: {
    backgroundColor: "bg.error",
    descriptionColor: "fg.muted",
    iconColor: "fg.error",
    titleColor: "fg.error",
  },
  errorSecond: {
    backgroundColor: "bg.error",
    descriptionColor: "fg.muted",
    iconColor: "fg.error",
    titleColor: "text.fg",
  },
  errorSolid: {
    backgroundColor: "fg.error",
    descriptionColor: "fg.inverted",
    iconBgColor: "bg.subtle",
    iconColor: "fg.error",
    titleColor: "fg.inverted",
  },
  info: {
    backgroundColor: "bg.subtle",
    borderColor: "gray.200",
    descriptionColor: "fg.muted",
    iconBgColor: "bg",
    iconColor: "redOrange.500",
  },
  subtle: {
    backgroundColor: "bg.subtle",
    descriptionColor: "fg.muted",
    iconBgColor: "bg",
    iconColor: "redOrange.500",
  },
  subtleWarning: {
    backgroundColor: "bg.subtle",
    descriptionColor: "fg.muted",
    iconBgColor: "bg",
    iconColor: "fg.warning",
  },
  success: {
    backgroundColor: "bg.success",
    descriptionColor: "fg.muted",
    iconBgColor: "bg.subtle",
    iconColor: "fg.success",
  },
  warning: {
    backgroundColor: "bg.warning",
    descriptionColor: "fg.muted",
    iconColor: "fg.warning",
  },
};

type Props = {
  children?: ReactNode;
  description: ReactNode;
  iconName?: AppIconName;
  title: string;
  variant: BannerVariant;
};

export const Banner = ({
  children,
  description,
  iconName,
  title,
  variant,
}: Props) => {
  const config = variantMap[variant];
  return (
    <HStack
      alignItems="flex-start"
      bg={config.backgroundColor}
      border={config.borderColor && "1px solid"}
      borderColor={config.borderColor}
      borderRadius="3xl"
      gap="3"
      p="4"
    >
      {iconName && (
        <Icon
          bg={config.iconBgColor || "bg.subtle"}
          color={config.iconColor}
          name={iconName}
          variant="frame"
        />
      )}
      <VStack alignItems="flex-start" gap="3">
        <VStack alignItems="flex-start" gap="1">
          <Typography color={config.titleColor} textStyle="subtitle">
            {title}
          </Typography>
          {typeof description === "string" ? (
            <Typography
              color={config.descriptionColor || "text.fg_subtle"}
              textStyle="caption"
            >
              {description}
            </Typography>
          ) : (
            description
          )}
        </VStack>
        {children}
      </VStack>
    </HStack>
  );
};
